import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trophy, Star, Zap, Shield, Crown, Target, Award, 
  Flame, Rocket, Medal, Diamond, Gift, Sparkles
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

// Achievement Context
const AchievementContext = createContext(null);

export const useAchievements = () => {
  const context = useContext(AchievementContext);
  if (!context) {
    throw new Error('useAchievements must be used within AchievementProvider');
  }
  return context;
};

// Achievement definitions
const ACHIEVEMENTS = {
  // Navigation achievements
  DASHBOARD_UNLOCKED: {
    id: 'dashboard_unlocked',
    title: 'Command Center Unlocked',
    description: 'First time accessing the dashboard',
    icon: Target,
    color: '#00F5FF',
    xp: 50,
    category: 'navigation'
  },
  TREASURY_UNLOCKED: {
    id: 'treasury_unlocked',
    title: 'Treasury Unlocked',
    description: 'Accessed the wallet management',
    icon: Diamond,
    color: '#FFD700',
    xp: 50,
    category: 'navigation'
  },
  BATTLE_LOG_UNLOCKED: {
    id: 'battle_log_unlocked',
    title: 'Battle Historian',
    description: 'Viewed the battle log',
    icon: Shield,
    color: '#9945FF',
    xp: 50,
    category: 'navigation'
  },
  INTEL_UNLOCKED: {
    id: 'intel_unlocked',
    title: 'Intel Master',
    description: 'Accessed employee intel center',
    icon: Zap,
    color: '#00FF88',
    xp: 75,
    category: 'navigation'
  },
  
  // Action achievements
  FIRST_DEPOSIT: {
    id: 'first_deposit',
    title: 'First Blood',
    description: 'Processed your first deposit',
    icon: Flame,
    color: '#FF6B00',
    xp: 100,
    category: 'action'
  },
  FIRST_WITHDRAWAL: {
    id: 'first_withdrawal',
    title: 'Cash Out Master',
    description: 'Processed your first withdrawal',
    icon: Rocket,
    color: '#FF3366',
    xp: 100,
    category: 'action'
  },
  PLAYER_ADDED: {
    id: 'player_added',
    title: 'Recruiter',
    description: 'Added a new player to the guild',
    icon: Star,
    color: '#FFD700',
    xp: 75,
    category: 'action'
  },
  MESSAGE_SENT: {
    id: 'message_sent',
    title: 'Communicator',
    description: 'Sent your first message',
    icon: Award,
    color: '#9945FF',
    xp: 50,
    category: 'action'
  },
  
  // Milestone achievements
  TEN_DEPOSITS: {
    id: 'ten_deposits',
    title: 'Deposit Veteran',
    description: 'Processed 10 deposits',
    icon: Medal,
    color: '#00F5FF',
    xp: 250,
    category: 'milestone'
  },
  HUNDRED_PLAYERS: {
    id: 'hundred_players',
    title: 'Guild Master',
    description: 'Manage 100+ players',
    icon: Crown,
    color: '#FFD700',
    xp: 500,
    category: 'milestone'
  },
  STREAK_WEEK: {
    id: 'streak_week',
    title: 'Weekly Warrior',
    description: 'Maintained a 7-day login streak',
    icon: Flame,
    color: '#FF6B00',
    xp: 200,
    category: 'milestone'
  },
  
  // Special achievements
  NIGHT_OWL: {
    id: 'night_owl',
    title: 'Night Owl',
    description: 'Working after midnight',
    icon: Star,
    color: '#9945FF',
    xp: 100,
    category: 'special'
  },
  SPEED_DEMON: {
    id: 'speed_demon',
    title: 'Speed Demon',
    description: 'Processed 5 transactions in 1 minute',
    icon: Zap,
    color: '#00F5FF',
    xp: 150,
    category: 'special'
  },
  EXPORT_MASTER: {
    id: 'export_master',
    title: 'Export Master',
    description: 'First export to CSV/PDF',
    icon: Gift,
    color: '#00FF88',
    xp: 75,
    category: 'action'
  }
};

// Achievement Provider Component
export function AchievementProvider({ children }) {
  const [unlockedAchievements, setUnlockedAchievements] = useState([]);
  const [pendingNotifications, setPendingNotifications] = useState([]);
  const [totalXP, setTotalXP] = useState(0);

  // Load unlocked achievements from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('casinoops_achievements');
    if (saved) {
      const data = JSON.parse(saved);
      setUnlockedAchievements(data.unlocked || []);
      setTotalXP(data.totalXP || 0);
    }
  }, []);

  // Save to localStorage when achievements change
  useEffect(() => {
    localStorage.setItem('casinoops_achievements', JSON.stringify({
      unlocked: unlockedAchievements,
      totalXP
    }));
  }, [unlockedAchievements, totalXP]);

  // Unlock achievement
  const unlock = useCallback((achievementKey) => {
    const achievement = ACHIEVEMENTS[achievementKey];
    if (!achievement) return false;
    
    // Check if already unlocked
    if (unlockedAchievements.includes(achievement.id)) return false;
    
    // Unlock it
    setUnlockedAchievements(prev => [...prev, achievement.id]);
    setTotalXP(prev => prev + achievement.xp);
    
    // Add to notification queue
    setPendingNotifications(prev => [...prev, achievement]);
    
    return true;
  }, [unlockedAchievements]);

  // Check if achievement is unlocked
  const isUnlocked = useCallback((achievementKey) => {
    const achievement = ACHIEVEMENTS[achievementKey];
    if (!achievement) return false;
    return unlockedAchievements.includes(achievement.id);
  }, [unlockedAchievements]);

  // Dismiss notification
  const dismissNotification = useCallback(() => {
    setPendingNotifications(prev => prev.slice(1));
  }, []);

  // Get achievement by key
  const getAchievement = useCallback((achievementKey) => {
    return ACHIEVEMENTS[achievementKey];
  }, []);

  // Get all achievements with unlock status
  const getAllAchievements = useCallback(() => {
    return Object.entries(ACHIEVEMENTS).map(([key, achievement]) => ({
      ...achievement,
      key,
      isUnlocked: unlockedAchievements.includes(achievement.id)
    }));
  }, [unlockedAchievements]);

  // Calculate level from XP
  const getLevel = useCallback(() => {
    if (totalXP < 100) return 1;
    if (totalXP < 300) return 2;
    if (totalXP < 600) return 3;
    if (totalXP < 1000) return 4;
    if (totalXP < 1500) return 5;
    if (totalXP < 2500) return 6;
    if (totalXP < 4000) return 7;
    if (totalXP < 6000) return 8;
    if (totalXP < 10000) return 9;
    return 10;
  }, [totalXP]);

  const value = {
    unlock,
    isUnlocked,
    dismissNotification,
    getAchievement,
    getAllAchievements,
    getLevel,
    totalXP,
    unlockedCount: unlockedAchievements.length,
    totalCount: Object.keys(ACHIEVEMENTS).length,
    currentNotification: pendingNotifications[0] || null
  };

  return (
    <AchievementContext.Provider value={value}>
      {children}
      <AchievementNotification />
    </AchievementContext.Provider>
  );
}

// Achievement Notification Toast
function AchievementNotification() {
  const { currentNotification, dismissNotification } = useAchievements();
  const { theme } = useTheme();

  useEffect(() => {
    if (currentNotification) {
      const timer = setTimeout(dismissNotification, 4000);
      return () => clearTimeout(timer);
    }
  }, [currentNotification, dismissNotification]);

  return (
    <AnimatePresence>
      {currentNotification && (
        <motion.div
          initial={{ opacity: 0, y: -100, x: '-50%', scale: 0.8 }}
          animate={{ opacity: 1, y: 0, x: '-50%', scale: 1 }}
          exit={{ opacity: 0, y: -100, x: '-50%', scale: 0.8 }}
          className="fixed top-6 left-1/2 z-[200] pointer-events-auto"
          data-testid="achievement-notification"
        >
          <motion.div
            animate={{ 
              boxShadow: [
                `0 0 20px ${currentNotification.color}40`,
                `0 0 40px ${currentNotification.color}60`,
                `0 0 20px ${currentNotification.color}40`
              ]
            }}
            transition={{ repeat: Infinity, duration: 2 }}
            className={cn(
              "relative overflow-hidden rounded-2xl border-2 p-4 min-w-[320px]",
              theme === 'dark' 
                ? "bg-[#0d0d15] border-white/20" 
                : "bg-white border-gray-200 shadow-2xl"
            )}
            style={{ borderColor: currentNotification.color }}
            onClick={dismissNotification}
          >
            {/* Background Glow */}
            <div 
              className="absolute inset-0 opacity-20"
              style={{ 
                background: `radial-gradient(circle at center, ${currentNotification.color}40 0%, transparent 70%)` 
              }}
            />
            
            {/* Sparkle Effects */}
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ 
                    opacity: [0, 1, 0],
                    scale: [0, 1, 0],
                    x: Math.random() * 300 - 150,
                    y: Math.random() * 100 - 50
                  }}
                  transition={{ 
                    delay: i * 0.2,
                    duration: 1.5,
                    repeat: Infinity
                  }}
                  style={{ 
                    left: '50%', 
                    top: '50%',
                    color: currentNotification.color
                  }}
                >
                  <Sparkles className="w-4 h-4" />
                </motion.div>
              ))}
            </div>
            
            <div className="relative flex items-center gap-4">
              {/* Icon */}
              <motion.div
                animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
                transition={{ duration: 0.5, repeat: 3 }}
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{ 
                  background: `linear-gradient(135deg, ${currentNotification.color}20, ${currentNotification.color}40)`,
                  border: `2px solid ${currentNotification.color}`
                }}
              >
                <currentNotification.icon 
                  className="w-7 h-7" 
                  style={{ color: currentNotification.color }} 
                />
              </motion.div>
              
              {/* Content */}
              <div className="flex-1">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <p className="text-xs uppercase tracking-wider text-[#FFD700] font-bold flex items-center gap-1">
                    <Trophy className="w-3 h-3" />
                    Achievement Unlocked!
                  </p>
                  <p className={cn(
                    "font-bold text-lg",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>
                    {currentNotification.title}
                  </p>
                  <p className={cn(
                    "text-sm",
                    theme === 'dark' ? "text-gray-400" : "text-gray-500"
                  )}>
                    {currentNotification.description}
                  </p>
                </motion.div>
              </div>
              
              {/* XP Badge */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring" }}
                className="flex flex-col items-center"
              >
                <div 
                  className="text-2xl font-black"
                  style={{ color: currentNotification.color }}
                >
                  +{currentNotification.xp}
                </div>
                <span className="text-xs text-gray-400 uppercase">XP</span>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Floating Deposit Button Component
export function FloatingDepositButton({ onClick, isVisible = true }) {
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [pulseEffect, setPulseEffect] = useState(true);

  useEffect(() => {
    // Pulse for attention every 30 seconds
    const interval = setInterval(() => {
      setPulseEffect(true);
      setTimeout(() => setPulseEffect(false), 3000);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      className="fixed bottom-6 right-6 z-50"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.9 }}
            className={cn(
              "absolute bottom-16 right-0 p-4 rounded-2xl border backdrop-blur-xl min-w-[200px]",
              theme === 'dark' 
                ? "bg-[#0d0d15]/95 border-white/10" 
                : "bg-white/95 border-gray-200 shadow-2xl"
            )}
          >
            <p className={cn(
              "text-sm font-semibold mb-2",
              theme === 'dark' ? "text-white" : "text-gray-900"
            )}>Quick Deposit</p>
            <p className={cn(
              "text-xs mb-3",
              theme === 'dark' ? "text-gray-400" : "text-gray-500"
            )}>Scan QR or tap to process deposit</p>
            
            {/* Mini QR placeholder */}
            <div className={cn(
              "w-full aspect-square rounded-xl border flex items-center justify-center mb-3",
              theme === 'dark' ? "bg-white/5 border-white/10" : "bg-gray-50 border-gray-200"
            )}>
              <div className="grid grid-cols-4 gap-1 w-16 h-16">
                {[...Array(16)].map((_, i) => (
                  <div 
                    key={i} 
                    className={cn(
                      "rounded-sm",
                      Math.random() > 0.5 
                        ? theme === 'dark' ? "bg-white" : "bg-gray-900"
                        : "bg-transparent"
                    )} 
                  />
                ))}
              </div>
            </div>
            
            <button
              onClick={onClick}
              className="w-full py-2 rounded-xl bg-gradient-to-r from-[#00FF88] to-[#00F5FF] text-black font-bold text-sm hover:opacity-90 transition-opacity"
            >
              Process Deposit
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      
      <motion.button
        onClick={onClick}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        animate={pulseEffect ? {
          boxShadow: [
            '0 0 0 0 rgba(0, 255, 136, 0.7)',
            '0 0 0 15px rgba(0, 255, 136, 0)',
          ]
        } : {}}
        transition={pulseEffect ? { duration: 1, repeat: 3 } : {}}
        className={cn(
          "w-14 h-14 rounded-full flex items-center justify-center shadow-2xl relative overflow-hidden",
          "bg-gradient-to-br from-[#00FF88] to-[#00F5FF]"
        )}
        data-testid="floating-deposit-btn"
      >
        {/* Shine effect */}
        <motion.div
          animate={{ x: [-100, 100] }}
          transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"
        />
        
        <span className="text-2xl font-bold text-black relative">$</span>
      </motion.button>
    </motion.div>
  );
}

// Call Notification Component
export function CallNotification({ call, onAnswer, onDecline }) {
  const { theme } = useTheme();
  const [isRinging, setIsRinging] = useState(true);

  useEffect(() => {
    // Play ringing sound
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onp2VkImDgIGEiY+Tl5WSkI2Kh4aGiIqMkJOTkZCNi4mIiImKjI6QkZGQj42MioqKiouMjY6Pj4+Ojo2MjIuLi4yMjY2Ojo6NjY2MjIyMjI2NjY2NjY2NjY2NjI2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2NjY2Njo6Ojo6Oj4+Pj4+Pj4+Pj4+Pj4+Pj4+P');
    audio.loop = true;
    if (isRinging) {
      audio.play().catch(() => {});
    }
    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, [isRinging]);

  if (!call) return null;

  const handleAnswer = () => {
    setIsRinging(false);
    onAnswer();
  };

  const handleDecline = () => {
    setIsRinging(false);
    onDecline();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: -50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8, y: -50 }}
        className="fixed top-6 left-1/2 -translate-x-1/2 z-[150]"
        data-testid="call-notification"
      >
        <motion.div
          animate={isRinging ? { 
            scale: [1, 1.02, 1],
            boxShadow: [
              '0 0 20px rgba(0, 255, 136, 0.3)',
              '0 0 40px rgba(0, 255, 136, 0.5)',
              '0 0 20px rgba(0, 255, 136, 0.3)'
            ]
          } : {}}
          transition={{ repeat: Infinity, duration: 1 }}
          className={cn(
            "rounded-3xl border-2 p-6 min-w-[350px] backdrop-blur-xl",
            theme === 'dark' 
              ? "bg-[#0d0d15]/95 border-[#00FF88]/50" 
              : "bg-white/95 border-green-400 shadow-2xl"
          )}
        >
          {/* Header */}
          <div className="flex items-center gap-4 mb-4">
            <motion.div
              animate={isRinging ? { rotate: [-10, 10, -10] } : {}}
              transition={{ repeat: Infinity, duration: 0.3 }}
              className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00FF88] to-[#00F5FF] flex items-center justify-center"
            >
              <span className="text-2xl">📞</span>
            </motion.div>
            
            <div className="flex-1">
              <p className="text-xs text-[#00FF88] uppercase tracking-wider font-bold mb-1">
                {isRinging ? 'Incoming Call' : 'Connected'}
              </p>
              <p className={cn(
                "font-bold text-xl",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>
                {call.from}
              </p>
              <p className={cn(
                "text-sm",
                theme === 'dark' ? "text-gray-400" : "text-gray-500"
              )}>
                {call.type || 'Voice Call'}
              </p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleDecline}
              className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#FF3366] to-[#FF6B00] text-white font-bold flex items-center justify-center gap-2"
            >
              <span>✕</span> Decline
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleAnswer}
              animate={isRinging ? { scale: [1, 1.05, 1] } : {}}
              transition={{ repeat: Infinity, duration: 0.8 }}
              className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#00FF88] to-[#00F5FF] text-black font-bold flex items-center justify-center gap-2"
            >
              <span>✓</span> Answer
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export { ACHIEVEMENTS };
export default AchievementProvider;
